import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  selectableFiscalYears,
  type CaptureSource,
  type Receipt,
  type Transaction,
} from '@budgetmonkey/shared'
import { useCurrentUser } from '@/auth/AuthProvider'
import { useTransactions } from '@/features/transactions/hooks'
import { usePeriods } from '@/features/reconciliation/hooks'
import {
  convertHeicForUpload,
  deleteReceipt,
  fetchReceiptsWithProblems,
  linkReceiptToTransaction,
  purgeReceiptRecord,
  rejectUploadReason,
  reprocessReceipt,
  unlinkReceiptFromTransaction,
  updateReceipt,
  uploadReceipt,
  UploadRejected,
  type ReceiptsResult,
  type ReceiptUpdate,
} from './api'
import {
  drainQueue,
  enqueueCapture,
  fileFromPending,
  isOffline,
  listPending,
  queueAvailable,
  QUEUE_CHANGED_EVENT,
  type PendingCapture,
} from './offlineQueue'
import { humanErrorMessage } from '@/lib/errorMessage'
import { reportProblem } from '@/lib/problemReports'
import { withDeadline } from '@/lib/withTimeout'

const receiptsKey = (userId: string) => ['receipts', userId] as const

/**
 * How long a capture may sit unread before this stops watching for it.
 *
 * The pipeline is upload, then a Storage trigger, then the vision call. The
 * model itself takes two to four seconds; measured end to end against real
 * captures, the result lands between four and ninety seconds later, the gap
 * being Eventarc delivery and a cold start. Ten minutes is far past any of
 * that, and a capture still unread by then is not coming — something failed
 * without saying so, and polling for it forever would be a tab quietly
 * talking to Firestore all afternoon.
 */
const READING_PATIENCE_MS = 10 * 60 * 1000

/** A capture whose reading has not landed yet, and is young enough to wait for. */
function stillWorthWatching(receipt: Receipt, now: number): boolean {
  const status = receipt.extraction?.status ?? null
  // `null` is a capture whose document exists before the function has written
  // anything — the first seconds after an upload.
  if (status !== null && status !== 'pending') return false
  return now - new Date(receipt.capturedAt).getTime() < READING_PATIENCE_MS
}

export function useReceipts() {
  const user = useCurrentUser()
  const query = useQuery({
    queryKey: receiptsKey(user.uid),
    queryFn: () => withDeadline(fetchReceiptsWithProblems(user.uid)),
    /**
     * Ask again while something is still being read.
     *
     * Nothing told the screen when a reading finished. The extraction runs in
     * a Cloud Function and writes to Firestore; the app reads Firestore once
     * and caches it, so "Reading the receipt…" stayed on screen long after
     * the answer had arrived — and pressing "Try reading it again" appeared
     * instant because that path invalidates the cache, revealing a result
     * that had been sitting there for a minute. Reported 2026-11-02.
     *
     * Only while something is actually pending, so a settled screen makes no
     * requests at all.
     */
    refetchInterval: (query) => {
      const receipts = query.state.data?.receipts ?? []
      const now = Date.now()
      return receipts.some((receipt) => stillWorthWatching(receipt, now)) ? 4000 : false
    },
  })
  return {
    ...query,
    data: query.data?.receipts,
    unreadable: query.data?.unreadable ?? [],
  }
}

/**
 * Receipts not yet attached to a transaction — the review queue (§7.1 step 7).
 * This is the list that should feel like an inbox: everything here is work
 * waiting to be done.
 */
export function useUnlinkedReceipts() {
  const query = useReceipts()
  const data = useMemo(
    () => (query.data ?? []).filter((r) => r.linkedTransactionId === null),
    [query.data],
  )
  return { ...query, data }
}

/**
 * The receipts behind one charge.
 *
 * Derived from `transactions.receiptIds`, the same side the packet is built
 * from and the receipts screen reports — not from `receipt.linkedTransactionId`,
 * which holds one id. A bill covering two charges is only ever the *primary*
 * of one of them, so reading the link showed it on the first charge and left
 * the second saying "nothing attached yet" — while the receipts screen, the
 * packet, and the printed PDF all had it on both. Reported on a Northern Air
 * invoice split across a $620.99 part and a $41.41 shipping charge.
 */
export function useReceiptsForTransaction(transactionId: string | undefined) {
  const query = useReceipts()
  const chargesByReceipt = useChargesByReceipt()
  const data = useMemo(
    () =>
      (query.data ?? []).filter((receipt) =>
        (chargesByReceipt.get(receipt.id ?? '') ?? []).some(
          (charge) => charge.id === transactionId,
        ),
      ),
    [query.data, chargesByReceipt, transactionId],
  )
  return { ...query, data }
}

/**
 * Every charge each receipt is filed against, keyed by receipt id.
 *
 * Read from `transactions.receiptIds` rather than from the receipt's own
 * `linkedTransactionId`, for two reasons that both matter here.
 *
 * One: `linkedTransactionId` holds a single id, and a bill can cover more than
 * one charge — a hotel that bills the deposit and the balance separately is
 * two charges and one piece of paper. The schema calls that field the
 * *primary* transaction for exactly that reason.
 *
 * Two: `transactions.receiptIds` is what the packet is assembled from. If the
 * two sides ever disagree, the packet's view is the one that decides what gets
 * printed and handed in, so it is the one the receipts screen should report.
 * Reading the other side is how a receipt could sit under "waiting to be
 * filed" while already printing behind a log sheet line.
 */
export function useChargesByReceipt() {
  // Across years, not just the one selected. A receipt has no fiscal year of
  // its own — the charge does — so scoping this to the year on screen made a
  // filed receipt read "waiting to be filed" whenever its charge sat in
  // another year. Which is precisely the June-charge-on-a-July-statement case
  // the year picker exists for.
  //
  // Three fixed calls rather than a loop, because the count has to be constant
  // across renders. `selectableFiscalYears` returns exactly three, and they
  // are the same three every screen already loads.
  const years = selectableFiscalYears()
  const first = useTransactions(years[0])
  const second = useTransactions(years[1])
  const third = useTransactions(years[2])

  const transactions = useMemo(
    () => [...(first.data ?? []), ...(second.data ?? []), ...(third.data ?? [])],
    [first.data, second.data, third.data],
  )

  return useMemo(() => {
    const byReceipt = new Map<string, Transaction[]>()
    for (const transaction of transactions) {
      for (const receiptId of transaction.receiptIds) {
        const charges = byReceipt.get(receiptId)
        if (charges === undefined) byReceipt.set(receiptId, [transaction])
        else charges.push(transaction)
      }
    }
    return byReceipt
  }, [transactions])
}

export function useUpdateReceipt() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ReceiptUpdate }) =>
      updateReceipt(id, patch),
    onSuccess: () => client.invalidateQueries({ queryKey: receiptsKey(user.uid) }),
  })
}

/**
 * Attach a receipt to a transaction.
 *
 * Invalidates transactions as well as receipts: the link is written to both
 * documents, so refreshing only this feature's cache would leave the ledger
 * showing a transaction with no receipt attached until the next reload.
 */
export function useLinkReceipt() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      receiptId,
      transactionId,
    }: {
      receiptId: string
      transactionId: string
    }) => linkReceiptToTransaction(receiptId, transactionId),
    /**
     * The link is written into the caches here, and the refetch is started
     * rather than awaited.
     *
     * `invalidateQueries` resolves only once the refetches it triggers have
     * come back — here the receipts list and three fiscal years of charges.
     * Awaiting it made filing a receipt wait for four round trips before the
     * panel closed, which on a phone is the "Filing…" that sits there for a
     * really long time, reported 2026-11-02.
     *
     * Not awaiting it alone was worse than slow, though: the panel closed over
     * a list that still said the receipt was waiting to be filed, which reads
     * as a save that failed. Filing it a second time then created a second
     * charge for one purchase, with every duplicate check silent because the
     * first charge was missing from the same stale cache. So both sides of the
     * link are corrected in place before the refetch confirms them — the panel
     * still closes in about a tenth of a second, over a list that is right.
     */
    onSuccess: (_result, { receiptId, transactionId }) => {
      client.setQueriesData<ReceiptsResult>(
        { queryKey: receiptsKey(user.uid) },
        (current) =>
          current === undefined
            ? current
            : {
                ...current,
                receipts: current.receipts.map((r) =>
                  // `linkedTransactionId` is set only when unset — the
                  // receipt's first charge keeps it, as the write does.
                  r.id === receiptId && r.linkedTransactionId === null
                    ? { ...r, linkedTransactionId: transactionId }
                    : r,
                ),
              },
      )
      client.setQueriesData<Transaction[]>(
        { queryKey: ['transactions', user.uid] },
        (current) =>
          current?.map((t) =>
            t.id === transactionId && !(t.receiptIds ?? []).includes(receiptId)
              ? { ...t, receiptIds: [...(t.receiptIds ?? []), receiptId] }
              : t,
          ),
      )
      void client.invalidateQueries({ queryKey: receiptsKey(user.uid) })
      void client.invalidateQueries({ queryKey: ['transactions', user.uid] })
    },
  })
}

/** Detach a receipt from one charge, leaving any others it is on alone. */
export function useUnlinkReceipt() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      receipt,
      transactionId,
    }: {
      receipt: Receipt
      transactionId: string
    }) => unlinkReceiptFromTransaction(receipt, transactionId),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: receiptsKey(user.uid) }),
        client.invalidateQueries({ queryKey: ['transactions', user.uid] }),
      ])
    },
  })
}

/**
 * Re-read a receipt whose extraction failed.
 *
 * The call resolves only once the server has finished reading, so the button
 * stays in its pending state for the whole attempt rather than appearing to
 * succeed instantly and changing nothing.
 */
export function useReprocessReceipt() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (receiptId: string) => reprocessReceipt(receiptId),
    onSuccess: () => client.invalidateQueries({ queryKey: receiptsKey(user.uid) }),
  })
}

/** Clear a record too malformed to render, so its warning isn't permanent. */
export function usePurgeReceiptRecord() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (receiptId: string) => purgeReceiptRecord(user.uid, receiptId),
    onSuccess: () => client.invalidateQueries({ queryKey: receiptsKey(user.uid) }),
  })
}

export function useDeleteReceipt() {
  const user = useCurrentUser()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (receipt: Receipt) => deleteReceipt(receipt),
    onSuccess: () => client.invalidateQueries({ queryKey: receiptsKey(user.uid) }),
  })
}

/**
 * The offline capture queue — §6.2.
 *
 * Drains at mount and on every `online` event. There is deliberately no
 * service worker involved: §6.2 rules out background sync for v1, so a queued
 * capture uploads the next time the app is open with a connection.
 *
 * Only one drain runs at a time. `drainQueue` reads the pending list, uploads,
 * and only then deletes each entry, so two overlapping runs both see the same
 * photo and both upload it — one receipt becomes two, and the duplicate is
 * only ever noticed by counting. Overlap is not exotic here: a phone regaining
 * signal fires `online` more than once, and it fires while the previous drain
 * is still pushing bytes up a weak link. Caught in testing, where React's
 * StrictMode double-invoked the mount effect and one queued photo arrived
 * twice, five milliseconds apart.
 */
export function useOfflineQueue() {
  const user = useCurrentUser()
  const client = useQueryClient()
  const [pending, setPending] = useState<PendingCapture[]>([])
  const [online, setOnline] = useState(() => navigator.onLine)
  const [draining, setDraining] = useState(false)
  // Refs, not the `draining` state: state settles a render later, so a second
  // trigger in the same tick would read it as false and start anyway.
  const drainingRef = useRef(false)
  const drainAgainRef = useRef(false)

  const refresh = useCallback(async () => {
    if (!queueAvailable()) return
    try {
      setPending(await listPending(user.uid))
    } catch {
      // A browser that won't open IndexedDB (private-mode Safari) simply has
      // no queue. Uploads still work when online; nothing else should break.
      setPending([])
    }
  }, [user.uid])

  const drain = useCallback(async () => {
    if (!queueAvailable() || isOffline()) return

    // A run already in flight owns the queue. The request isn't dropped
    // though — it's remembered and served by one more pass, so a capture
    // queued during a connection flap can't sit there waiting for the next
    // mount because its `online` event happened to land mid-drain.
    if (drainingRef.current) {
      drainAgainRef.current = true
      return
    }

    /** Whether another drain was asked for while this one ran, clearing it. */
    const takeRepeatRequest = (): boolean => {
      const requested = drainAgainRef.current
      drainAgainRef.current = false
      return requested
    }

    drainingRef.current = true
    takeRepeatRequest()
    setDraining(true)
    try {
      do {
        const result = await drainQueue(user.uid, (entry) =>
          uploadReceipt(user.uid, fileFromPending(entry), entry.captureSource).done,
        )
        if (result.uploaded > 0) {
          await client.invalidateQueries({ queryKey: receiptsKey(user.uid) })
        }
      } while (takeRepeatRequest() && !isOffline())
    } finally {
      drainingRef.current = false
      drainAgainRef.current = false
      setDraining(false)
      await refresh()
    }
  }, [user.uid, client, refresh])

  useEffect(() => {
    void refresh()
    void drain()

    const goOnline = () => {
      setOnline(true)
      void drain()
    }
    const goOffline = () => setOnline(false)

    const queueChanged = () => void refresh()

    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    window.addEventListener(QUEUE_CHANGED_EVENT, queueChanged)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
      window.removeEventListener(QUEUE_CHANGED_EVENT, queueChanged)
    }
  }, [refresh, drain])

  return { pending, online, draining, refresh, drain }
}

/**
 * Only browser captures can be queued — `email_forward` arrives server-side
 * and never passes through this code, so anything unexpected is treated as a
 * desktop upload rather than silently dropped.
 */
function offlineSourceFor(source: CaptureSource): PendingCapture['captureSource'] {
  return source === 'mobile_capture' ? 'mobile_capture' : 'desktop_upload'
}

/**
 * One file's progress through an upload.
 *
 * There is no `queued` status. A file that goes to the offline queue leaves
 * this list entirely and is reported by the pending-captures banner instead,
 * which reads IndexedDB — the record. A row here saying "saved on this device"
 * was a copy of that state, and it went stale the moment the queue drained:
 * the photo was uploaded and the row still said it was sitting on the phone.
 */
export interface UploadItem {
  id: string
  fileName: string
  sizeBytes: number
  status: 'uploading' | 'done' | 'error'
  progress: number
  error?: string
  receiptId?: string
}

/**
 * Manages a batch of concurrent uploads.
 *
 * Each file tracks its own state so one rejection doesn't discard the rest —
 * dropping eight receipts and having the whole batch fail because one was a
 * screenshot would be worse than useless.
 */
export function useReceiptUploads() {
  const user = useCurrentUser()
  const client = useQueryClient()
  const [items, setItems] = useState<UploadItem[]>([])

  const patch = useCallback((id: string, next: Partial<UploadItem>) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, ...next } : i)))
  }, [])

  /** Hand a file over to the offline queue, which reports it from then on. */
  const handOff = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id))
  }, [])

  const upload = useCallback(
    (files: readonly File[], captureSource: CaptureSource = 'desktop_upload') => {
      for (const original of files) {
        const id = `${original.name}-${original.size}-${crypto.randomUUID()}`
        setItems((prev) => [
          ...prev,
          {
            id,
            fileName: original.name,
            sizeBytes: original.size,
            status: 'uploading',
            progress: 0,
          },
        ])

        void (async () => {
        // Before anything else, including the offline queue — a queued HEIC
        // would be converted on a device that may no longer have the codec
        // handy, and the point is to convert while we are on the phone that
        // took the photo.
        // Conversion now refuses rather than silently handing back a HEIC the
        // server cannot read, so the failure has to be reported here — at the
        // moment of upload, where it can still be acted on.
        let file: File
        try {
          file = await convertHeicForUpload(original)
        } catch (cause) {
          patch(id, {
            status: 'error',
            error:
              cause instanceof Error ? cause.message : 'That photo could not be read.',
          })
          return
        }

        if (file !== original) {
          patch(id, { fileName: file.name, sizeBytes: file.size })
        }

        // A file that will never be accepted is rejected here, online or not.
        // Queueing it would defer the bad news until the user is nowhere near
        // the receipt.
        const rejection = rejectUploadReason(file)
        if (rejection !== null) {
          patch(id, { status: 'error', error: rejection.message })
          return
        }

        // Offline: the photo goes to IndexedDB and uploads on reconnect (§6.2).
        // Once queued it belongs to the queue: the row here is dropped and the
        // pending-captures banner takes over reporting it, so there is only one
        // account of where the photo is and it stays true through the drain.
        if (isOffline() && queueAvailable()) {
          try {
            await enqueueCapture(user.uid, file, offlineSourceFor(captureSource))
            handOff(id)
          } catch {
            patch(id, {
              status: 'error',
              error: "You're offline and this device can't hold the photo. Try again on a connection.",
            })
          }
          return
        }

        try {
          const handle = uploadReceipt(user.uid, file, captureSource, (fraction) =>
            patch(id, { progress: fraction }),
          )
          void handle.done
            .then((receipt) => {
              patch(id, { status: 'done', progress: 1, receiptId: receipt.id })
              void client.invalidateQueries({ queryKey: receiptsKey(user.uid) })
            })
            .catch((cause: unknown) => {
              void reportProblem(cause, {
                userId: user.uid,
                email: user.email ?? '',
                context: 'uploading a receipt',
              })
              patch(id, {
                status: 'error',
                // Translated, not passed through: a refused upload used to
                // print a storage path and an error code at the person least
                // able to read them. See lib/errorMessage.
                error: humanErrorMessage(
                  cause,
                  'Upload failed. The file is still on your device — try again.',
                ),
              })
            })
        } catch (cause) {
          // Reported here too, not only in the promise below. A failure that
          // threw on the way *into* the upload went unrecorded, which is how
          // a lost receipt on 2026-11-03 left no trace for anyone to read.
          if (!(cause instanceof UploadRejected)) {
            void reportProblem(cause, {
              userId: user.uid,
              email: user.email ?? '',
              context: 'starting a receipt upload',
            })
          }
          patch(id, {
            status: 'error',
            error:
              cause instanceof UploadRejected
                ? cause.message
                : humanErrorMessage(cause, 'Could not start the upload.'),
          })
        }
        })()
      }
    },
    [user.uid, user.email, patch, handOff, client],
  )

  const clearFinished = useCallback(() => {
    setItems((prev) => prev.filter((i) => i.status === 'uploading'))
  }, [])

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id))
  }, [])

  return { items, upload, clearFinished, dismiss }
}

export interface SubmittedCycle {
  periodId: string
  /** `June 2026` — how Matt refers to a cycle. */
  label: string
  /** ISO date, for sorting newest first. */
  cycleEnd: string
  receiptIds: ReadonlySet<string>
}

/**
 * Receipts that have been handed in, grouped by the packet that carried them.
 *
 * Derived, never stored. A packet already records exactly which charges it
 * covered and whether it was submitted, and a receipt reaches the business
 * office through its charges — so "already handed in" is a fact about the
 * packet, not a flag to keep in sync on the receipt. The same reason the
 * dashboard computes totals instead of counting them: a derived answer cannot
 * drift from the thing it describes.
 *
 * Only `submitted` counts. A generated packet can still be regenerated, and
 * hiding its receipts while it is being worked on would take them away exactly
 * when they are being checked.
 */
export function useSubmittedCycles(): SubmittedCycle[] {
  const { data: periods = [] } = usePeriods()
  const chargesByReceipt = useChargesByReceipt()

  return useMemo(() => {
    const submitted = periods.filter((period) => period.status === 'submitted')
    if (submitted.length === 0) return []

    // Charge id -> the submitted periods carrying it.
    const periodsByCharge = new Map<string, string[]>()
    for (const period of submitted) {
      for (const id of period.orderedTransactionIds) {
        const list = periodsByCharge.get(id)
        if (list === undefined) periodsByCharge.set(id, [period.id ?? ''])
        else list.push(period.id ?? '')
      }
    }

    const receiptsByPeriod = new Map<string, Set<string>>()
    for (const [receiptId, charges] of chargesByReceipt) {
      for (const charge of charges) {
        for (const periodId of periodsByCharge.get(charge.id ?? '') ?? []) {
          const set = receiptsByPeriod.get(periodId) ?? new Set<string>()
          set.add(receiptId)
          receiptsByPeriod.set(periodId, set)
        }
      }
    }

    return submitted
      .map((period) => ({
        periodId: period.id ?? '',
        label: cycleLabel(period.cycleEnd),
        cycleEnd: period.cycleEnd,
        receiptIds: receiptsByPeriod.get(period.id ?? '') ?? new Set<string>(),
      }))
      .filter((cycle) => cycle.receiptIds.size > 0)
      .sort((a, b) => b.cycleEnd.localeCompare(a.cycleEnd))
  }, [periods, chargesByReceipt])
}

/**
 * "June 2026" from a cycle end date, read in UTC.
 *
 * The cycle is named for the month holding most of it, which is the month
 * before the 25th-to-24th cycle ends — the same rule the log sheet header
 * follows, so a receipt and the packet it went out on agree on what to call it.
 */
function cycleLabel(cycleEnd: string): string {
  const date = new Date(cycleEnd)
  if (Number.isNaN(date.getTime())) return 'Unknown cycle'
  return date.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
}
